// A price book item's history in Setup (Blueprint): why a save changed what it did, kept with it
// (pricebook.updateItem's reason), the history a page at a time (pricebook.itemHistory), and putting one
// entry's old values back (pricebook.rollback), which needs its own why.
import { changeWords, type ItemChange, type ItemHistoryProps } from '@flowology/ui-web';
import type { Built } from './setup-model.ts';

/** How many changes a page reads. */
export const HISTORY_PAGE = 20;
export const REASON_MAX = 500;

/** A save's input with the person's why beside it, when they gave one. */
export function withReason(built: Built, reason: string): Built {
  const why = reason.trim();
  if ('error' in built || !why) return built;
  if (why.length > REASON_MAX) return { error: `Say why in ${REASON_MAX} characters or fewer.` };
  return { input: { ...built.input, reason: why } };
}

/** Putting an entry's old values back (pricebook.rollback): the item as the screen has it, and why. */
export function putBackInput(
  item: { id: string; rowVersion: number },
  changeId: string,
  reason: string,
): Built {
  const why = reason.trim();
  if (!why) return { error: 'Say why you’re putting them back: it’s kept in the history.' };
  if (why.length > REASON_MAX) return { error: `Say why in ${REASON_MAX} characters or fewer.` };
  return { input: { itemId: item.id, changeId, rowVersion: item.rowVersion, reason: why } };
}

/** What putting an entry back would do, a line a field: "Price $2,400.00 to $2,350.00". */
export const putBackLines = (entry: readonly ItemChange[]): string[] =>
  entry.map((c) =>
    changeWords({
      ...c,
      old: c.new,
      new: c.old,
      ...(c.category ? { category: { old: c.category.new, new: c.category.old } } : {}),
    }),
  );

const NAMES: Record<string, string> = {
  name: 'name',
  category_id: 'category',
  description: 'what it includes',
  unit: 'what it’s sold by',
  price_cents: 'price',
  member_price_cents: 'member price',
  cost_cents: 'our cost',
  taxable: 'whether it’s taxed',
  tax_class: 'tax',
  spiff_cents: 'spiff',
  active: 'whether it’s offered',
  code: 'code',
  type: 'kind',
  sellable: 'whether it’s for sale',
};
const and = (xs: string[]) =>
  xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;

/** pricebook.rollback's answer, said once: what came back, and what stayed as it is and why. */
export function putBackDone(r: {
  restored: string[];
  kept: { field: string; why: 'cost' | 'not_by_hand' }[];
}): string {
  const back = and(r.restored.map((f) => NAMES[f] ?? f));
  const said = [`Put back its ${back}.`];
  const cost = r.kept.filter((k) => k.why === 'cost');
  const fixed = r.kept.filter((k) => k.why === 'not_by_hand');
  if (cost.length) said.push('Our cost stays as it is: only those who see our cost put it back.');
  if (fixed.length)
    said.push(
      `Its ${and(fixed.map((k) => NAMES[k.field] ?? k.field))} stays as it is: it isn’t set by hand.`,
    );
  return said.join(' ');
}

/** The next page under the ones already read. */
export const morePage = (had: ItemHistoryProps, page: ItemHistoryProps): ItemHistoryProps => ({
  changes: [...had.changes, ...page.changes.filter((c) => !had.changes.some((h) => h.id === c.id))],
  next: page.next,
});
